import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { supabase } from '@/lib/supabaseClient';
import type { Profile } from '@/lib/supabaseClient';
import type { ClassRoom, Student, VacationSlot } from '@/lib/types';
import { DAY_NAMES } from '@/lib/constants';
import Modal from '@/components/base/Modal';
import StudentRosterPicker from '@/components/feature/StudentRosterPicker';

type Props = {
  open: boolean;
  slot: VacationSlot | null;
  classes: ClassRoom[];
  teachers: Profile[];
  students: Student[];
  classStudentIdsByClass: Record<string, string[]>;
  slotStudentIds: string[];
  academyId: string | null;
  termId: string | null;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
};

export default function VacationSlotFormModal({ open, slot, classes, teachers, students, classStudentIdsByClass, slotStudentIds, academyId, termId, onClose, onSaved, onDeleted }: Props) {
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [teacherId, setTeacherId] = useState('');
  const [classId, setClassId] = useState('');
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const lastSlotId = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      lastSlotId.current = null;
      return;
    }
    if (slot && lastSlotId.current === slot.id) return;
    lastSlotId.current = slot?.id ?? null;
    setName(slot?.name || '');
    setSubject(slot?.subject || '');
    setTeacherId(slot?.teacher_id || '');
    setClassId(slot?.class_id || '');
    setDayOfWeek(slot?.day_of_week ?? 1);
    setStartTime(slot?.start_time?.slice(0, 5) || '09:00');
    setEndTime(slot?.end_time?.slice(0, 5) || '10:00');
    // 항목에 별도 명단이 있으면 그 명단을, 없으면 연결된 반 명단을 체크해 둬요.
    if (slot) {
      const ids = slot.students_customized
        ? slotStudentIds
        : slot.class_id
          ? classStudentIdsByClass[slot.class_id] || []
          : [];
      setSelectedIds(new Set(ids));
    } else {
      setSelectedIds(new Set());
    }
    setFormError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, slot]);

  function toggleStudent(studentId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) next.delete(studentId);
      else next.add(studentId);
      return next;
    });
  }

  // 반을 연결하면 이름·과목·담당 선생님과 함께 학생 명단도 그 반 값으로 채워줘요.
  function handleClassChange(value: string) {
    setClassId(value);
    const cls = classes.find((c) => c.id === value);
    if (cls) {
      setName(cls.name);
      setSubject(cls.subject || '');
      setTeacherId(cls.teacher_id || '');
    }
    setSelectedIds(new Set(classStudentIdsByClass[value] || []));
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!name.trim()) {
      setFormError('수업 이름을 입력해 주세요.');
      return;
    }
    if (endTime <= startTime) {
      setFormError('종료 시간은 시작 시간보다 늦어야 해요.');
      return;
    }
    setSaving(true);
    setFormError('');

    const payload = {
      academy_id: academyId || null,
      term_id: termId || null,
      class_id: classId || null,
      name: name.trim(),
      subject: subject.trim() || null,
      teacher_id: teacherId || null,
      day_of_week: dayOfWeek,
      start_time: startTime,
      end_time: endTime,
      students_customized: true,
    };

    try {
      let slotId = slot?.id || null;
      if (slot) {
        const res = await supabase.from('vacation_timetable').update(payload).eq('id', slot.id);
        if (res.error) throw res.error;
      } else {
        const res = await supabase
          .from('vacation_timetable')
          .insert(payload)
          .select('id')
          .maybeSingle();
        if (res.error) throw res.error;
        slotId = (res.data as { id: string } | null)?.id || null;
        if (!slotId) throw new Error('생성된 항목을 찾을 수 없어요.');
      }

      if (slotId) {
        const { error: delError } = await supabase
          .from('vacation_timetable_students')
          .delete()
          .eq('slot_id', slotId);
        if (delError) throw delError;
        if (selectedIds.size > 0) {
          const rows = Array.from(selectedIds).map((studentId) => ({
            slot_id: slotId as string,
            student_id: studentId,
          }));
          const { error: insError } = await supabase
            .from('vacation_timetable_students')
            .insert(rows);
          if (insError) throw insError;
        }
      }
      onSaved();
    } catch (err) {
      setFormError('저장에 실패했어요. 잠시 후 다시 시도해 주세요.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!slot) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('vacation_timetable').delete().eq('id', slot.id);
      if (error) throw error;
      onDeleted();
    } catch (err) {
      setFormError('삭제에 실패했어요. 잠시 후 다시 시도해 주세요.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  const inputClass =
    'w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-200';

  return (
    <Modal open={open} title={slot ? '방학특강 수업 수정' : '방학특강 수업 추가'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground-700">
            수업 이름 <span className="text-accent-600">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="예: 방학특강 수학 A반"
            className={inputClass}
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground-700">
            반 연결 (선택)
          </label>
          <select
            value={classId}
            onChange={(e) => handleClassChange(e.target.value)}
            className={inputClass}
          >
            <option value="">연결 안 함</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.subject ? ` · ${c.subject}` : ''}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-foreground-400">
            반을 연결하면 학생 명단이 함께 표시돼요.
          </p>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-foreground-700">학생 명단</label>
            <span className="rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-700">
              {selectedIds.size}명
            </span>
          </div>
          <p className="mb-2 mt-1 text-xs text-foreground-400">
            체크해서 이 특강에 들어갈 학생을 넣고 뺄 수 있어요. 여기서 바꾼 명단은 이 특강에만
            적용돼요.
          </p>
          <div className="rounded-md border border-background-200 p-1">
            <StudentRosterPicker
              students={students}
              selectedIds={selectedIds}
              onToggle={toggleStudent}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-700">과목</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="예: 수학"
              className={inputClass}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-700">
              담당 선생님
            </label>
            <select
              value={teacherId}
              onChange={(e) => setTeacherId(e.target.value)}
              className={inputClass}
            >
              <option value="">선택 안 함</option>
              {teachers.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.full_name || '이름 없음'}
                  {t.role === 'owner' ? ' (원장)' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-700">요일</label>
            <select
              value={dayOfWeek}
              onChange={(e) => setDayOfWeek(Number(e.target.value))}
              className={inputClass}
            >
              {DAY_NAMES.map((d, i) => (
                <option key={i} value={i}>
                  {d}요일
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-700">시작</label>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-700">종료</label>
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        {formError && (
          <div className="rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-800">
            {formError}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 pt-1">
          {slot ? (
            <button
              type="button"
              onClick={handleDelete}
              disabled={saving}
              className="cursor-pointer whitespace-nowrap rounded-md border border-accent-300 px-4 py-2 text-sm font-medium text-accent-700 transition-colors hover:bg-accent-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              삭제
            </button>
          ) : (
            <span></span>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={saving}
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? '저장 중...' : '저장'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}